import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { constants as cons } from '../../Constants/constants'

import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import WarningIcon from '@mui/icons-material/Warning'
import CancelIcon from '@mui/icons-material/Cancel'
import { makeStyles } from '@mui/styles'
import { getString } from '../../helper/util'

import variables from '../../styles/globalVariables.scss'
import { Clear, Lens } from '@mui/icons-material'

const { blackColor, subItemColor, middleGrayColor } = variables

const iconTextStyle = makeStyles(() => ({
    icon: {
        '& .MuiIconButton-root': {
        color: `${subItemColor}`,
        },
    },
}))

const normalTextStyle = makeStyles(() => ({
    label: {
        '& .MuiFormLabel-root': {
        color: `${blackColor}`,
        },
        '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `${blackColor}4D`,
        },
        '& .MuiFormLabel-root.Mui-disabled': {
        color: 'rgba(18, 18, 18, 0.54)',
        },
    },
}))

const trueValueStyle = makeStyles(() => ({
    label: {
        '& .MuiInputBase-root': {
        color: `#2e7d32`,
        },
        '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `#4caf50`,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4caf50',
        },
    },
}))

/**
 * TempMultiAutoSelectInput
 * Props: inputSetting: p, index, handleChange, dataList
 * - supports `p.multiple = true` behavior as a dedicated multi-select component
 */
export const TempMultiAutoSelectInput = ({ inputSetting: p, index, handleChange, dataList }) => {
    const [localValue, setLocalValue] = useState([])
    const iconTextStyleClasses = iconTextStyle()
    const normalTextStyleClasses = normalTextStyle()
    const trueTextStyleClasses = trueValueStyle()
    const lang = useSelector((state) => state.lang.ui.lang)
    const theme = useSelector((state) => state.theme.ui.theme)

    if (!p.className) {
        p.className = 'edit-create-input'
    }

    if (!p.helperTextIcon) {
        p.helperTextIcon = true
    }

    if (!p.required) {
        p.required = false
    }

    const getLabelStyle = () => {
        if (p.disableStatusThemeColor) return ''
        switch (p.value) {
        case true:
        case cons.STATUS?.active:
            return trueTextStyleClasses.label
        default:
            return ''
        }
    }

    const getOptionLabel = (option) => {
        let optionLabel = ''
        if (option) {
        if (option.name) {
            if (option.name.key) {
            optionLabel = getString(lang, option.name.resource, option.name.key, theme)
            } else {
            optionLabel = option.name
            }
        }
        }
        return optionLabel
    }

    useEffect(() => {
        // normalize p.value (which may be array of primitives or array of option objects) to array of option objects
        if (p.multiple) {
        const selects = p.selectValue || []
        if (!Array.isArray(p.value)) {
            setLocalValue([])
            return
        }

        if (p.value.length === 0) {
            setLocalValue([])
            return
        }

        // if elements are objects with value property, assume they are option objects
        if (typeof p.value[0] === 'object' && p.value[0] !== null && p.value[0].hasOwnProperty('value')) {
            setLocalValue(p.value)
            return
        }

        // otherwise assume primitives and map to option objects from selectValue
        const mapped = selects.filter((opt) => p.value.includes(opt.value))
        setLocalValue(mapped)
        } else {
        setLocalValue([])
        }
    }, [p.value, p.selectValue, p.multiple]) // eslint-disable-line react-hooks/exhaustive-deps

    const isOptionEqualToValue = (option, value) => {
        if (!option) return false
        if (!value) return false
        // value may be object or primitive
        if (typeof value === 'object' && value !== null) return option.value === value.value
        return option.value === value
    }

    return (
        <Autocomplete
        size={p.size || cons.SIZE.SMALL}
        multiple={true}
        filterSelectedOptions={true}
        options={p.selectValue || []}
        groupBy={(option) => option.group}
        getOptionDisabled={(option) => option.isDisabled}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        renderTags={(value, getTagProps) =>
            value.map((option, idx) => (
            <Chip
                {...getTagProps({ index: idx })}
                key={idx}
                label={getOptionLabel(option)}
                size='small'
            />
            ))
        }
        renderOption={(props, option) => {
            return (
            <li {...props} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap' }}>
                <div>
                {option ? (option.name ? (option.name.key ? getString(lang, option.name.resource, option.name.key, theme) : option.name) : '') : ''}
                {option.tooltip && (
                    <Tooltip
                    className="white-space special-button-margin-left"
                    title={<span style={{ whiteSpace: 'pre-line', fontSize: '12px' }}>{option.tooltip}</span>}
                    arrow
                    >
                    <IconButton style={{ padding: 0, color: '#fce903' }} size={cons.SIZE.SMALL}>
                        <WarningIcon />
                    </IconButton>
                    </Tooltip>
                )}
                {option.tableTypeName && <span style={{ color: `${middleGrayColor}` }}>{option.tableTypeName}</span>}
                </div>
                {option.hexList && option.hexList.map((hex, idx) => <Lens key={`${hex}-${idx}`} sx={{ color: hex || '#fff' }} />)}
            </li>
            )
        }}
        className={`edit-select-input ${p.className} ${iconTextStyleClasses.icon} ${normalTextStyleClasses.label} ${getLabelStyle()}`}
        value={localValue || []}
        onChange={(event, newValue) => {
            // newValue is an array of option objects
            handleChange && handleChange(p.name, newValue ? newValue.map((input) => input.value) : [], index, dataList)
        }}
        disableClearable={p.disableClearable || p.customEndAdornmentClearIcon}
        disabled={p.disabled}
        onInputChange={(event, newInputValue) => {
            // basic freeSolo handling for multiple: if freeSolo and user types a new value and confirms,
            // Autocomplete's onChange will provide it as a string in the newValue array; leave handling to onChange.
            if (p.freeSolo && !p.multiple) {
            // keep parity with original single-select behavior
            if (newInputValue !== localValue?.value) {
                if (localValue?.value === null && p.isColorFieldSpecialCase) {
                handleChange && handleChange(p.name, localValue?.value || newInputValue, index, dataList)
                } else if (p.freeSoloUppercase) {
                handleChange && handleChange(p.name, newInputValue.toUpperCase(), index, dataList)
                } else {
                handleChange && handleChange(p.name, newInputValue, index, dataList)
                }
            }
            }
        }}
        renderInput={(params) => (
            <TextField
            {...params}
            label={getString(lang, p.label.resource, p.label.key, theme)}
            variant={cons.STYLE.VARIANT.OUTLINED}
            helperText={
                p.helperText &&
                p.helperText.length > 0 && (
                <span className="error-message">
                    {p.helperTextIcon && <CancelIcon className="error-message-icon" fontSize={cons.SIZE.SMALL} />}
                    {p.helperText}
                </span>
                )
            }
            multiline={p.multiline}
            required={p.required}
            InputProps={{
                ...params.InputProps,
                endAdornment: p.endAdornmentIconButton ? (
                <>
                    {params.InputProps.endAdornment}
                    <IconButton color={cons.STYLE.PRIMARY} size={cons.SIZE.SMALL}>
                    {p.endAdornmentIconButton}
                    </IconButton>
                    {p.customEndAdornmentClearIcon && (
                    <IconButton onClick={() => handleChange && handleChange(p.name, null, index, dataList)} size={cons.SIZE.SMALL}>
                        <Clear fontSize={cons.SIZE.SMALL} />
                    </IconButton>
                    )}
                </>
                ) : (
                params.InputProps.endAdornment
                ),
            }}
            />
        )}
        />
    )
}

export default TempMultiAutoSelectInput
